import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { UserProfileInfo } from './UserProfileInfo';
import {
  getUserMessageCount,
  FREE_MESSAGE_LIMIT,
} from '@/lib/subscription';
import { useAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { useSubscriptionWithTier } from '@/hooks/useSubscription';

const FREE_TIER_LIMIT = FREE_MESSAGE_LIMIT;

export default function StatisticsPage() {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();
  
  // Use the subscription hook for tier information
  const { 
    isActive, 
    tierName,
  } = useSubscriptionWithTier();

  useEffect(() => {
    async function loadMessageCount() {
      try {
        let count = 0;
        
        try {
          count = await getUserMessageCount(user?.id);
          logger.debug('Message count loaded: ' + count);
        } catch (countError) {
          logger.error('Error loading message count:', countError);
        }
        
        setMessageCount(count);
      } catch (error) {
        logger.error('Error loading message count:', error);
        toast({
          title: 'Error',
          description: 'Failed to load message count information. Please try again later.',
          variant: 'destructive',
        });
      }
    }
    
    // Only load message count
    if (user?.id) {
      loadMessageCount();
    }
  }, [toast, user?.id]);

  // Check if user is on free tier
  const isFreeTier = () => {
    return !isActive || tierName === 'Free';
  };

  // Function to refresh message count
  const refreshMessageCount = async () => {
    try {
      logger.debug('Refreshing message count');
      setIsActionLoading(true);
      
      // Always force refresh to get the latest count from the database
      const refreshedCount = await getUserMessageCount(user?.id, true);
      logger.debug('Message count refreshed', { count: refreshedCount });
      
      setMessageCount(refreshedCount);
      toast({
        title: "Count Updated",
        description: `Your message count is now ${refreshedCount}`,
      });
      
      return refreshedCount;
    } catch (error) {
      logger.error("Error refreshing message count:", error);
      toast({
        title: "Error",
        description: "Failed to refresh message count. See console for details.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl pb-20 md:pb-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Statistics</h1>
        <p className="text-gray-500 dark:text-gray-300">
          View your usage statistics and account activity
        </p>
      </div>
      
      {/* Message Usage - moved from Subscription page */}
      <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Message Usage</CardTitle>
          <CardDescription className="text-gray-500 dark:text-gray-300">
            Track your monthly message usage
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-base font-medium text-gray-900 dark:text-white">
                {messageCount} {messageCount === 1 ? 'message' : 'messages'} this month
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {isFreeTier() ? `${FREE_TIER_LIMIT - messageCount} remaining` :
                 'Unlimited messages available'}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={refreshMessageCount} 
              disabled={isActionLoading}
              className="flex items-center gap-1 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              {isActionLoading ? <LoadingSpinner className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
              <span>Refresh</span>
            </Button>
          </div>
          
          {isFreeTier() && (
            <div className="bg-gray-50 dark:bg-gray-700/80 p-4 rounded-lg border border-gray-100 dark:border-gray-600">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                You're on the free tier with {FREE_TIER_LIMIT} messages per month.
                Upgrade your subscription for unlimited messages.
              </p>
            </div>
          )}
          
          {/* Progress bar for free tier */}
          {isFreeTier() && (
            <div className="w-full">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>Usage</span>
                <span>{Math.round((messageCount / FREE_TIER_LIMIT) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-[#F37022] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((messageCount / FREE_TIER_LIMIT) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Account Statistics - moved from Account page */}
      <Card className="border dark:border-gray-700 shadow-sm dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Account Statistics</CardTitle>
          <CardDescription className="text-gray-500 dark:text-gray-300">
            View your account activity and usage patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UserProfileInfo />
        </CardContent>
      </Card>
    </div>
  );
}